import {Injectable, OnDestroy} from '@angular/core';
import {defer, firstValueFrom, map, Observable, of} from 'rxjs';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {Project, ProjectDownloader, ProjectDownloadService} from '@mydocma/project';
import {MmDbService} from '..//mm-db/mm-db-service';
import {ProjectHttpService as MmProjectService} from '@docma-mm-core/project/project-http.service';
import {MmProjectDbCollections, MmProjectDbSchema} from '..//mm-db/mm-project-db-schema';
import {FieldService} from '@docma-mm-core/field/field.service';
import {FieldMinimal} from '@docma-mm-core/field/field';
import {CraftService} from '@docma-mm-core/craft/craft.service';
import {OrderService} from '@docma-mm-core/order/order.service';
import {ContactService} from '@docma-mm-core/contact/contact.service';
import {ClassificationService} from '@docma-mm-core/classification/classification.service';
import {UserDefinedApiService} from '@docma-mm-core/user-defined/user-defined-api.service';
import {StatusService} from '@docma-mm-core/status/status.service';
import {KeywordService} from '@docma-core/keyword/keyword.service';
import {ProjectOfflineService} from '@docma-mm-core/project/project-offline.service';
import {LayoutApiService} from '@docma-mm-core/layout/layout-api.service';
import {Layout} from '@docma-mm-core/layout/layout';
import {StructureService} from '@docma-mm-core/structure/structure.service';
import {Structure} from '@docma-mm-core/structure/structure';
import {BrowserDbService} from '../mm-db/browser-db.service';
import {constants} from '@docma-mm-core/constants';


@Injectable()
export class MmProjectDownloadService implements ProjectDownloader, OnDestroy {

  constructor(private readonly mmDbService: MmDbService,
              private readonly fieldService: FieldService,
              private readonly craftService: CraftService,
              private readonly orderService: OrderService,
              private readonly contactService: ContactService,
              private readonly projectDownloadService: ProjectDownloadService,
              private readonly classificationService: ClassificationService,
              private readonly userDefinedService: UserDefinedApiService,
              private readonly statusService: StatusService,
              private readonly keywordService: KeywordService,
              private readonly layoutService: LayoutApiService,
              private readonly structureService: StructureService,
              private readonly offlineProjectService: ProjectOfflineService,
              private readonly httpProjectService: MmProjectService) {

            this.projectDownloadService.registerDownloader(this);

  }
  module = 'mm';
  download(project: Project): Observable<Project> {
    return this.downloadProject(project, true).pipe(map(mp=> project));
  }
  canRemove(projec: Project): Observable<boolean> {
    throw new Error("Method not implemented.");
  }
  remove(project: Project): Observable<Project> {
    throw new Error("Method not implemented.");
  }

  ngOnDestroy(): void {
  }

  public downloadProject(project: Project, forceRefresh: boolean = false): Observable<MmProject> {

    return defer(async () => {

      if (!project) {
        return null;
      }

      let mmProject: MmProject = await this.offlineProjectService.getProjectByMydocmaId(project.id).toPromise();

      if (mmProject && !forceRefresh) {
        return mmProject;
      }

      try {
        mmProject = await this.httpProjectService.getProjectByMydocmaId(project.id).toPromise();
        if (mmProject.Structure) {
          mmProject.Structure.Name1 = mmProject.Name1;
          mmProject.Structure.Name2 = mmProject.Name2;
        }

      } catch (error) {
        console.log('Cannot download mm project with muid ' + project.id);
      }
      if (!mmProject) {
        return null;
      }
      const db = await this.mmDbService.getProjectDb(project.id);
      await db.setAll(MmProjectDbCollections.project  as never, [mmProject]);

      await Promise.all([
        firstValueFrom(this.downloadFields(mmProject, true)),
        firstValueFrom(this.downloadLayouts(mmProject, true)),
        this.downloadCrafts(mmProject, db),
        this.downloadOrders(mmProject, db),
        this.downloadContacts(mmProject, db),
        this.downloadClassifications(mmProject, db),
        this.downloadUserDefineds(mmProject, db),
        this.downloadStatuses(mmProject, db),
        this.downloadKeywords(mmProject, db),
      ]);

      return mmProject;
    });

  }

  public downloadFields(project: MmProject, forceRefresh: boolean = false): Observable<FieldMinimal[]> {
    return defer(async () => {

      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let fields;
      if (!forceRefresh) {
        fields = await db.getAll(MmProjectDbCollections.fields as never);
        if (fields && fields.length > 0) {
          return fields;
        }
      }
      const loaded = await this.fieldService.getProjectFields(project.Id,true, this.fieldService.groupFields, ['AttributeGroup_Image', 'Structure_LongPath'], 10000).toPromise();
      const structureLongPath = loaded.Data.find(x => x.Id === 'Structure_LongPath');

      if (loaded) {
      const structureField: FieldMinimal = {
        Id: 'Structure',
        Global: structureLongPath.Project || structureLongPath.Global,
        Permission: 1,
        Prop: 'Structure'
      };
      loaded.Data.push(structureField);
    }
      fields = loaded && loaded.Data ? loaded.Data : []
      await db.setAll(MmProjectDbCollections.fields as never, fields);
      return fields;
  });
  }

  private async downloadCrafts(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    const crafts = await firstValueFrom(this.craftService.getProjectCrafts(mmProject.Id, constants.MaxInt));
    await db.setAll(MmProjectDbCollections.crafts as never, crafts && crafts.Data ? crafts.Data : []);
  }

  private async downloadOrders(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    // Download orders
    const orders = await firstValueFrom(this.orderService.getProjectOrders(mmProject.Id, constants.MaxInt));
    await db.setAll(MmProjectDbCollections.orders as never, orders && orders.Data ? orders.Data : []);
  }

  private async downloadContacts(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    // Download contacts
    const contacts = await firstValueFrom(this.contactService.getProjectContacts(mmProject.Id, constants.MaxInt));
    await db.setAll(MmProjectDbCollections.contacts as never, contacts && contacts.Data ? contacts.Data : []);
  }

  private async downloadClassifications(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    // Download classifications
    const classifications = await this.classificationService.getProjectClassifications(mmProject.Id).toPromise();
    await db.setAll(MmProjectDbCollections.classifications as never, []);
    if (classifications) {
      Promise.all(Object.keys(classifications).map(async key => {
        db.set(MmProjectDbCollections.classifications as never, classifications[key], key);
      })
      );
    }
  }

  private async downloadUserDefineds(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    // Download user defineds
    const userDefineds = await this.userDefinedService.getUserDefineds(mmProject.Id).toPromise();
    await db.setAll(MmProjectDbCollections.userDefineds as never, []);
    if (userDefineds) {
      Promise.all(Object.keys(userDefineds).map(async key => {
        db.set(MmProjectDbCollections.userDefineds as never, userDefineds[key], key);
      })
      );
    }
  }

  private async downloadStatuses(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    // Download statuses
    const statuses = await this.statusService.getProjectStatusList(mmProject.Id, 1000).toPromise();
    await db.setAll(MmProjectDbCollections.statuses as never, statuses && statuses.Data ? statuses.Data : []);
  }

  private async downloadKeywords(mmProject: MmProject, db: BrowserDbService<MmProjectDbSchema>) {
    // Download keywords
    const keywords =  await this.keywordService.getKeywordsByProjectId(mmProject.Id).toPromise();
    await db.setAll(MmProjectDbCollections.keywords as never, keywords  ? keywords : []);
  }


  public downloadLayouts(project: MmProject, forceRefresh: boolean = false): Observable<Layout[]> {
    if (!project) {
      return of([]);
    }

    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let layouts;
      if (!forceRefresh) {
        layouts = await db.getAll(MmProjectDbCollections.layouts as never);
        if (layouts && layouts.length > 0) {
          return layouts;
        }
      }
      layouts =  await this.layoutService.getProjectLayouts(project.Id, 1000).toPromise();

      await db.setAll(MmProjectDbCollections.layouts as never, layouts.Data);
      return layouts.Data;
    });
  }

  public downloadLayout(project: MmProject, layoutId: any, forceRefresh = false): Observable<Layout> {

    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      const layout = await db.get(MmProjectDbCollections.layouts as never, layoutId);
      if (!forceRefresh) {
        if (layout && layout.Fields && layout.Fields.length > 0) {
          return layout;
        }
      }
      const fields = await this.layoutService.getLayoutFields(project.Id, layoutId).toPromise();
      layout.Fields = fields.sort((a,b)=> a.SortId - b.SortId);

      await db.set(MmProjectDbCollections.layouts as never, layout);

      return layout;
    });
  }


  public downloadStructures(project: MmProject, forceRefresh: boolean = false): Observable<Structure[]> {
    return defer(async () => {
      const db = await this.mmDbService.getProjectDb(project.MyDocmaUid);
      let structures;
      if (!forceRefresh) {
         return await db.getAll(MmProjectDbCollections.structures as never);
      }
      const  loaded =  await this.structureService.getProjectStructures(project.Id).toPromise();
      structures = this.structureService.loadStructureTree(loaded.Data);
      await db.setAll(MmProjectDbCollections.structures as never, structures);
      return structures;
    });
  }

  private loadStructureTree() {

  }

}
